<template>
  <div class="sovi-vuokrasopimus-page sovi-home">
    <router-link to="/" class="sovi-vuokrasopimus-page__logo">
      <sovi-logo :size="48" />
    </router-link>

    <v-menu offset-y>
      <template #activator="{ props }">
        <v-btn
          class="language-menu"
          size="default"
          color="#eca1c2"
          v-bind="props"
        >
          <template #prepend>
            <v-icon icon="mdi-web" />
          </template>

          {{ $i18n.locale.toUpperCase() }}
        </v-btn>
      </template>

      <v-list>
        <v-list-item
          v-for="option in $i18n.availableLocales"
          :key="option"
          class="align-items-center"
          @click="onLocaleChange(option)"
        >
          <v-row align="center" no-gutters>
            {{ option.toUpperCase() }}
          </v-row>
        </v-list-item>
      </v-list>
    </v-menu>

    <sovi-content-nav />

    <!-- Hero section with parallax -->
    <sovi-home-card
      id="vuokrasopimus-hero"
      background="/images/vuokrasopimus-1.webp"
      src="/images/vuokrasopimus-2.webp"
      :title="t('heroTitle')"
      fixed
      style="padding-top: 100px"
    >
      <strong>{{ t('heroLead') }}</strong>

      <p>{{ t('heroText1') }}</p>

      <p>{{ t('heroText2') }}</p>

      <p>{{ t('heroText3') }}</p>
    </sovi-home-card>

    <!-- Why sign electronically -->
    <sovi-home-card
      id="miksi"
      src="/images/content-7.webp"
      :title="t('whyTitle')"
      flipped
    >
      <p>{{ t('whyText1') }}</p>

      <p>{{ t('whyText2') }}</p>

      <p>{{ t('whyListIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('whyBenefit1') }}</li>

        <li>{{ t('whyBenefit2') }}</li>

        <li>{{ t('whyBenefit3') }}</li>

        <li>{{ t('whyBenefit4') }}</li>

        <li>{{ t('whyBenefit5') }}</li>

        <li>{{ t('whyBenefit6') }}</li>

        <li>{{ t('whyBenefit7') }}</li>
      </ul>

      <p>{{ t('whyText3') }}</p>
    </sovi-home-card>

    <!-- Process steps -->
    <sovi-home-card
      id="prosessi"
      src="/images/content-8.webp"
      :title="t('processTitle')"
    >
      <p>{{ t('processIntro') }}</p>

      <ol class="ms-6 list-decimal">
        <li>{{ t('processStep1') }}</li>

        <li>{{ t('processStep2') }}</li>

        <li>{{ t('processStep3') }}</li>

        <li>{{ t('processStep4') }}</li>

        <li>{{ t('processStep5') }}</li>

        <li>{{ t('processStep6') }}</li>
      </ol>

      <p>{{ t('processOutro') }}</p>
    </sovi-home-card>

    <!-- Sign anywhere -->
    <sovi-home-card
      id="paikasta-riippumatta"
      src="/images/content-9.webp"
      :title="t('anywhereTitle')"
      flipped
    >
      <p>{{ t('anywhereText1') }}</p>

      <p>{{ t('anywhereListIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('anywhereCase1') }}</li>

        <li>{{ t('anywhereCase2') }}</li>

        <li>{{ t('anywhereCase3') }}</li>

        <li>{{ t('anywhereCase4') }}</li>

        <li>{{ t('anywhereCase5') }}</li>
      </ul>

      <p>{{ t('anywhereOutro') }}</p>
    </sovi-home-card>

    <!-- Document types -->
    <sovi-home-card
      id="asiakirjat"
      src="/images/content-10.webp"
      :title="t('documentsTitle')"
    >
      <p>{{ t('documentsIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('doc1') }}</li>

        <li>{{ t('doc2') }}</li>

        <li>{{ t('doc3') }}</li>

        <li>{{ t('doc4') }}</li>

        <li>{{ t('doc5') }}</li>

        <li>{{ t('doc6') }}</li>

        <li>{{ t('doc7') }}</li>

        <li>{{ t('doc8') }}</li>

        <li>{{ t('doc9') }}</li>

        <li>{{ t('doc10') }}</li>

        <li>{{ t('doc11') }}</li>
      </ul>

      <p>{{ t('documentsOutro') }}</p>
    </sovi-home-card>

    <!-- Residential lease -->
    <sovi-home-card
      id="asuinhuoneisto"
      src="/images/content-11.webp"
      :title="t('residentialTitle')"
      flipped
    >
      <p>{{ t('residentialText1') }}</p>

      <p>{{ t('residentialText2') }}</p>

      <p>{{ t('residentialText3') }}</p>
    </sovi-home-card>

    <!-- Commercial lease -->
    <sovi-home-card
      id="toimitila"
      src="/images/content-24.webp"
      :title="t('commercialTitle')"
    >
      <p>{{ t('commercialText1') }}</p>

      <p>{{ t('commercialText2') }}</p>

      <p>{{ t('commercialText3') }}</p>
    </sovi-home-card>

    <!-- Comparison table -->
    <sovi-home-card
      id="vertailu"
      src="/images/content-1.webp"
      :title="t('comparisonTitle')"
      flipped
    >
      <v-table>
        <thead>
          <tr>
            <th>{{ t('tableFeature') }}</th>

            <th>{{ t('tablePaper') }}</th>

            <th>{{ t('tableElectronic') }}</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>{{ t('tableRemoteSigning') }}</td>

            <td>{{ t('tableDifficult') }}</td>

            <td>{{ t('tableEasy') }}</td>
          </tr>

          <tr>
            <td>{{ t('tablePrinting') }}</td>

            <td>{{ t('tableOftenNeeded') }}</td>

            <td>{{ t('tableNotNeeded') }}</td>
          </tr>

          <tr>
            <td>{{ t('tableScanning') }}</td>

            <td>{{ t('tableOftenNeeded') }}</td>

            <td>{{ t('tableNotNeeded') }}</td>
          </tr>

          <tr>
            <td>{{ t('tableSending') }}</td>

            <td>{{ t('tableEmailOrPost') }}</td>

            <td>{{ t('tableOnlineRequest') }}</td>
          </tr>

          <tr>
            <td>{{ t('tableTracking') }}</td>

            <td>{{ t('tableManual') }}</td>

            <td>{{ t('tableRealtime') }}</td>
          </tr>

          <tr>
            <td>{{ t('tableArchiving') }}</td>

            <td>{{ t('tablePaperFolder') }}</td>

            <td>{{ t('tableDigitalManagement') }}</td>
          </tr>

          <tr>
            <td>{{ t('tableFindingLater') }}</td>

            <td>{{ t('tableCanBeSlow') }}</td>

            <td>{{ t('tableFast') }}</td>
          </tr>

          <tr>
            <td>{{ t('tableTenantExperience') }}</td>

            <td>{{ t('tableVaries') }}</td>

            <td>{{ t('tableSmooth') }}</td>
          </tr>
        </tbody>
      </v-table>

      <p>{{ t('comparisonOutro') }}</p>
    </sovi-home-card>

    <!-- Document management -->
    <sovi-home-card
      id="hallinta"
      src="/images/content-2.webp"
      :title="t('managementTitle')"
    >
      <p>{{ t('managementText1') }}</p>

      <p>{{ t('managementText2') }}</p>

      <p>{{ t('managementListIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('managementItem1') }}</li>

        <li>{{ t('managementItem2') }}</li>

        <li>{{ t('managementItem3') }}</li>

        <li>{{ t('managementItem4') }}</li>

        <li>{{ t('managementItem5') }}</li>
      </ul>

      <p>{{ t('managementOutro') }}</p>
    </sovi-home-card>

    <!-- Target audiences -->
    <sovi-home-card
      id="kenelle"
      src="/images/content-3.webp"
      :title="t('audienceTitle')"
      flipped
    >
      <p>{{ t('audienceIntro') }}</p>

      <h3 class="home-title">{{ t('audiencePrivate') }}</h3>

      <p>{{ t('audiencePrivateText') }}</p>

      <h3 class="home-title">{{ t('audienceInvestors') }}</h3>

      <p>{{ t('audienceInvestorsText') }}</p>

      <h3 class="home-title">{{ t('audiencePropertyManagement') }}</h3>

      <p>{{ t('audiencePropertyManagementText') }}</p>

      <h3 class="home-title">{{ t('audienceAgents') }}</h3>

      <p>{{ t('audienceAgentsText') }}</p>

      <h3 class="home-title">{{ t('audienceCompanies') }}</h3>

      <p>{{ t('audienceCompaniesText') }}</p>
    </sovi-home-card>

    <!-- Common challenges -->
    <sovi-home-card
      id="haasteet"
      src="/images/content-4.webp"
      :title="t('challengesTitle')"
    >
      <p>{{ t('challengesText1') }}</p>

      <p>{{ t('challengesText2') }}</p>

      <p>{{ t('challengesText3') }}</p>
    </sovi-home-card>

    <!-- Example 1: Tenant in another city -->
    <sovi-home-card
      id="esimerkki-1"
      src="/images/content-5.webp"
      :title="t('example1Title')"
      flipped
    >
      <p>{{ t('example1Text1') }}</p>

      <p>{{ t('example1Text2') }}</p>
    </sovi-home-card>

    <!-- Example 2: Commercial lease -->
    <sovi-home-card
      id="esimerkki-2"
      src="/images/content-6.webp"
      :title="t('example2Title')"
    >
      <p>{{ t('example2Text1') }}</p>

      <p>{{ t('example2Text2') }}</p>
    </sovi-home-card>

    <!-- Legal validity -->
    <sovi-home-card
      id="patevyys"
      src="/images/content-12.webp"
      :title="t('validityTitle')"
      flipped
    >
      <p>{{ t('validityText1') }}</p>

      <p>{{ t('validityText2') }}</p>

      <p>{{ t('validityText3') }}</p>
    </sovi-home-card>

    <!-- Why choose Sovi.fi -->
    <sovi-home-card
      id="miksi-sovi"
      src="/images/content-13.webp"
      :title="t('whySoviTitle')"
    >
      <p>{{ t('whySoviIntro') }}</p>

      <ul class="ms-6 list-disc">
        <li>{{ t('whySoviItem1') }}</li>

        <li>{{ t('whySoviItem2') }}</li>

        <li>{{ t('whySoviItem3') }}</li>

        <li>{{ t('whySoviItem4') }}</li>

        <li>{{ t('whySoviItem5') }}</li>

        <li>{{ t('whySoviItem6') }}</li>
      </ul>

      <p>{{ t('whySoviOutro') }}</p>
    </sovi-home-card>

    <!-- FAQ section -->
    <sovi-home-card
      id="ukk"
      src="/images/content-14.webp"
      :title="t('faqTitle')"
      flipped
    >
      <v-expansion-panels>
        <v-expansion-panel :title="t('faq1Question')">
          <v-expansion-panel-text>
            {{ t('faq1Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq2Question')">
          <v-expansion-panel-text>
            {{ t('faq2Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq3Question')">
          <v-expansion-panel-text>
            {{ t('faq3Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq4Question')">
          <v-expansion-panel-text>
            {{ t('faq4Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq5Question')">
          <v-expansion-panel-text>
            {{ t('faq5Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq6Question')">
          <v-expansion-panel-text>
            {{ t('faq6Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq7Question')">
          <v-expansion-panel-text>
            {{ t('faq7Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel :title="t('faq8Question')">
          <v-expansion-panel-text>
            {{ t('faq8Answer') }}
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <p style="margin-top: 16px">
        {{ t('faqMoreText') }}
        <router-link
          to="/ukk"
          class="font-weight-medium text-decoration-underline text-black"
        >
          {{ t('faqMoreLink') }} →
        </router-link>
      </p>
    </sovi-home-card>

    <!-- CTA section -->
    <sovi-home-card
      id="aloita"
      src="/images/content-15.webp"
      :title="t('ctaTitle')"
    >
      <p>{{ t('ctaText1') }}</p>

      <p>{{ t('ctaText2') }}</p>

      <p>
        <strong>{{ t('ctaCta') }}</strong>
      </p>

      <template #button>
        <div style="display: grid; gap: 8px">
          <router-link
            to="/sahkoinen-allekirjoitus"
            class="font-weight-medium text-decoration-underline text-black"
          >
            {{ t('linkEsignature') }} →
          </router-link>

          <router-link
            to="/tyosopimuksen-sahkoinen-allekirjoitus"
            class="font-weight-medium text-decoration-underline text-black"
          >
            {{ t('linkEmploymentContract') }} →
          </router-link>

          <router-link
            to="/ukk"
            class="font-weight-medium text-decoration-underline text-black"
          >
            {{ t('linkFaq') }} →
          </router-link>
        </div>
      </template>
    </sovi-home-card>

    <div class="sovi-vuokrasopimus-page__login">
      <sovi-home-login />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTheme } from 'vuetify';
import { definePage } from 'vue-router/auto';
import { useRouteMeta } from '@/composables/useRouteMeta';
import { useAuthorizationStore } from '@/stores/authorization';
import { loadRecaptcha } from '@/plugins/recaptcha';
import SoviHomeLogin from '@/components/home/SoviHomeLogin.vue';

definePage({
  meta: {
    hero: false,
    restricted: false,
    maxContainer: true,
  },
});

useRouteMeta({
  title:
    'Vuokrasopimuksen sähköinen allekirjoitus – Allekirjoita vuokrasopimus verkossa | Sovi.fi',
  description:
    'Allekirjoita vuokrasopimus sähköisesti nopeasti ja turvallisesti. Sovi.fi helpottaa vuokrasopimusten lähettämistä, allekirjoittamista ja hallintaa verkossa.',
});

const { locale } = useI18n({ useScope: 'global' });

const { t } = useI18n();

const theme = useTheme();

const { isAuthorized } = useAuthorizationStore();

const onLocaleChange = (newLocale: string) => {
  locale.value = newLocale;
  localStorage.setItem('sovi-locale', newLocale);
};

onMounted(() => {
  document.getElementById('sovi-main')?.scrollTo(0, 0);
  window.scrollTo(0, 0);
  loadRecaptcha();

  const storedLocale = localStorage.getItem('sovi-locale');

  if (storedLocale && storedLocale !== locale.value) {
    locale.value = storedLocale;
  }

  theme.global.name.value = 'light';

  isAuthorized();
});
</script>

<style lang="scss">
.grecaptcha-badge {
  visibility: hidden;
}

.sovi-vuokrasopimus-page {
  position: relative;
  background-color: #fff;
  font-size: clamp(18px, 2vw, 22px);

  &__logo {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 2;
  }

  .language-menu {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 2;
    box-shadow:
      rgba(0, 0, 0, 0.15) 0px 15px 25px,
      rgba(0, 0, 0, 0.05) 0px 5px 10px;
  }

  .v-table {
    max-width: 100%;
    overflow-x: auto;
    display: block;
    font-size: clamp(12px, 1.5vw, 16px);
  }

  .home-title {
    line-height: 1.3;

    @media (min-width: 768px) {
      line-height: 1.1;
    }
    --title-color: rgb(var(--v-theme-primary));

    &.alt {
      --title-color: #eca1c2;
    }

    &::first-letter {
      padding-bottom: 2px;
      box-shadow: inset 0 calc(-1 * clamp(4px, 0.5vw, 6px)) 0 0
        var(--title-color);
    }
  }

  &__login {
    overflow: hidden;

    .sovi-home__login {
      &::before,
      &::after {
        display: none;
      }
    }
    background-color: #fff;
    padding: clamp(32px, 5vw, 64px) clamp(16px, 5vw, 32px);

    .sovi-home__login-form {
      display: grid;
      gap: clamp(16px, 3vw, 32px);
      position: relative;
      z-index: 1;

      h1 {
        font-size: clamp(24px, 3vw, 32px);
      }
    }
  }
}
</style>

<i18n lang="json">
{
  "fi": {
    "heroTitle": "Vuokrasopimuksen sähköinen allekirjoitus",
    "heroLead": "Allekirjoita vuokrasopimus sähköisesti ilman tulostamista tai tapaamista",
    "heroText1": "Vuokrasopimuksen sähköinen allekirjoitus tekee vuokraamisesta sujuvampaa sekä vuokranantajalle että vuokralaiselle. Sopimusta ei tarvitse tulostaa, postittaa, skannata tai allekirjoittaa fyysisessä tapaamisessa. Sen sijaan vuokrasopimus voidaan lähettää allekirjoitettavaksi verkossa, ja osapuolet voivat allekirjoittaa sen tietokoneella, tabletilla tai puhelimella.",
    "heroText2": "Sovi.fi tarjoaa suomalaisille yrityksille, vuokranantajille, isännöitsijöille ja kiinteistöalan toimijoille helpon tavan hoitaa vuokrasopimusten sähköinen allekirjoitus ja asiakirjahallinta yhdessä paikassa.",
    "heroText3": "Sähköinen allekirjoitus on erityisen hyödyllinen vuokrauksessa, jossa osapuolet eivät aina ole samalla paikkakunnalla. Vuokralainen voi allekirjoittaa sopimuksen etänä jo ennen muuttoa, ja vuokranantaja saa sopimuksen voimaan ilman ylimääräisiä viiveitä.",
    "whyTitle": "Miksi vuokrasopimus kannattaa allekirjoittaa sähköisesti?",
    "whyText1": "Vuokrasopimuksen allekirjoitus on usein kiireellinen vaihe. Asunnon, toimitilan tai muun vuokrakohteen vuokraaminen halutaan saada vahvistettua mahdollisimman nopeasti, jotta molemmat osapuolet voivat suunnitella seuraavat vaiheet.",
    "whyText2": "Perinteisessä paperiprosessissa sopimus voi viivästyä monesta syystä. Vuokralaisella ei ole tulostinta, allekirjoitus unohtuu, skannaus epäonnistuu tai sopimus jää sähköpostiketjun liitteeksi. Sähköinen allekirjoitus vähentää näitä ongelmia.",
    "whyListIntro": "Kun vuokrasopimus allekirjoitetaan sähköisesti:",
    "whyBenefit1": "sopimusprosessi nopeutuu",
    "whyBenefit2": "fyysistä tapaamista ei tarvita",
    "whyBenefit3": "sopimus voidaan allekirjoittaa etänä",
    "whyBenefit4": "asiakirjat pysyvät järjestyksessä",
    "whyBenefit5": "valmiit sopimukset löytyvät helposti",
    "whyBenefit6": "paperin käyttö vähenee",
    "whyBenefit7": "vuokralaisen kokemus paranee",
    "whyText3": "Sähköinen allekirjoitus sopii sekä yksittäisille vuokranantajille että organisaatioille, jotka käsittelevät suuria määriä vuokrasopimuksia.",
    "processTitle": "Näin vuokrasopimuksen sähköinen allekirjoitus toimii",
    "processIntro": "Vuokrasopimuksen allekirjoittaminen sähköisesti on suoraviivainen prosessi.",
    "processStep1": "Vuokrasopimus laaditaan tai valmis sopimuspohja ladataan Sovi.fi-palveluun.",
    "processStep2": "Vuokranantaja lisää allekirjoittajat, kuten vuokralaisen ja mahdolliset muut osapuolet.",
    "processStep3": "Allekirjoituspyyntö lähetetään sähköisesti.",
    "processStep4": "Vuokralainen avaa allekirjoituspyynnön ja allekirjoittaa sopimuksen verkossa.",
    "processStep5": "Muut osapuolet allekirjoittavat sopimuksen.",
    "processStep6": "Valmis vuokrasopimus tallentuu turvallisesti myöhempää käyttöä varten.",
    "processOutro": "Koko prosessi voidaan parhaimmillaan hoitaa saman päivän aikana. Tämä on suuri etu erityisesti silloin, kun vuokrasuhteen alkaminen on lähellä tai kohteesta on useita kiinnostuneita vuokralaisia.",
    "anywhereTitle": "Vuokrasopimus voidaan allekirjoittaa missä tahansa",
    "anywhereText1": "Sähköisen allekirjoituksen suurimpia etuja on paikkariippumattomuus. Vuokralainen voi allekirjoittaa sopimuksen kotona, työmatkalla tai toiselta paikkakunnalta. Vuokranantajan ei tarvitse järjestää erillistä tapaamista pelkkää allekirjoitusta varten.",
    "anywhereListIntro": "Tämä helpottaa esimerkiksi tilanteita, joissa:",
    "anywhereCase1": "vuokralainen muuttaa toiselta paikkakunnalta",
    "anywhereCase2": "vuokranantaja ei asu samalla alueella kuin vuokrakohde",
    "anywhereCase3": "vuokraus hoidetaan välittäjän kautta",
    "anywhereCase4": "toimitilaa vuokrataan yritykselle",
    "anywhereCase5": "sopimus halutaan vahvistaa nopeasti ennen avainten luovutusta",
    "anywhereOutro": "Sähköinen vuokrasopimus tekee vuokraamisesta joustavampaa ja vähentää turhaa odottelua.",
    "documentsTitle": "Mitä vuokraukseen liittyviä asiakirjoja voidaan allekirjoittaa sähköisesti?",
    "documentsIntro": "Sähköinen allekirjoitus soveltuu vuokrasopimusten lisäksi moniin muihin vuokraukseen liittyviin asiakirjoihin. Tyypillisiä esimerkkejä ovat:",
    "doc1": "asuinhuoneiston vuokrasopimus",
    "doc2": "toimitilan vuokrasopimus",
    "doc3": "määräaikainen vuokrasopimus",
    "doc4": "toistaiseksi voimassa oleva vuokrasopimus",
    "doc5": "vuokrasopimuksen jatkosopimus",
    "doc6": "vuokrasopimuksen muutos",
    "doc7": "vuokrankorotusilmoituksen hyväksyntä",
    "doc8": "irtisanomisasiakirjat",
    "doc9": "vakuuden palautukseen liittyvät dokumentit",
    "doc10": "avainten luovutuspöytäkirja",
    "doc11": "kuntotarkastuslomake",
    "documentsOutro": "Kun kaikki vuokraukseen liittyvät asiakirjat hallitaan samassa palvelussa, kokonaisuus pysyy selkeänä myös myöhemmin.",
    "residentialTitle": "Sähköinen allekirjoitus asuinhuoneiston vuokrasopimuksessa",
    "residentialText1": "Asuinhuoneiston vuokrauksessa nopeus ja selkeys ovat tärkeitä. Vuokralainen haluaa varmistaa asunnon, ja vuokranantaja haluaa luotettavan sopimuksen ennen vuokrasuhteen alkamista.",
    "residentialText2": "Sähköinen allekirjoitus helpottaa tätä vaihetta. Kun sopimus on laadittu, se voidaan lähettää vuokralaiselle heti allekirjoitettavaksi. Vuokralainen voi tarkistaa sopimuksen rauhassa ja hyväksyä sen sähköisesti ilman paperisia työvaiheita.",
    "residentialText3": "Tämä vähentää myös riskiä, että sopimus jää vahvistamatta tai allekirjoitus viivästyy muuton lähestyessä.",
    "commercialTitle": "Sähköinen allekirjoitus toimitilan vuokrasopimuksessa",
    "commercialText1": "Toimitilojen vuokrauksessa sopimukset voivat olla yksityiskohtaisempia kuin asuinhuoneistojen vuokrasopimukset. Sopimuksiin voi liittyä esimerkiksi käyttöoikeuksia, vastuunjakoa, liitteitä, muutostöitä, vuokra-aikoja ja irtisanomisehtoja.",
    "commercialText2": "Sähköinen allekirjoitus helpottaa toimitilavuokrausta erityisesti silloin, kun osapuolina on useita henkilöitä tai yrityksiä. Sopimus voidaan lähettää kaikille allekirjoittajille, ja prosessin etenemistä voidaan seurata keskitetysti.",
    "commercialText3": "Tämä auttaa varmistamaan, että sopimus saadaan valmiiksi ennen tilan käyttöönottoa.",
    "comparisonTitle": "Paperinen vai sähköinen vuokrasopimus?",
    "tableFeature": "Ominaisuus",
    "tablePaper": "Paperinen vuokrasopimus",
    "tableElectronic": "Sähköinen vuokrasopimus",
    "tableRemoteSigning": "Allekirjoitus etänä",
    "tableDifficult": "Hankalaa",
    "tableEasy": "Helppoa",
    "tablePrinting": "Tulostaminen",
    "tableOftenNeeded": "Usein tarvitaan",
    "tableNotNeeded": "Ei tarvita",
    "tableScanning": "Skannaaminen",
    "tableSending": "Sopimuksen lähetys",
    "tableEmailOrPost": "Sähköposti tai posti",
    "tableOnlineRequest": "Allekirjoituspyyntö verkossa",
    "tableTracking": "Prosessin seuranta",
    "tableManual": "Manuaalista",
    "tableRealtime": "Reaaliaikaista",
    "tableArchiving": "Arkistointi",
    "tablePaperFolder": "Paperikansio tai verkkolevy",
    "tableDigitalManagement": "Digitaalinen asiakirjahallinta",
    "tableFindingLater": "Sopimuksen löytäminen myöhemmin",
    "tableCanBeSlow": "Voi olla hidasta",
    "tableFast": "Nopeaa",
    "tableTenantExperience": "Vuokralaisen käyttökokemus",
    "tableVaries": "Vaihtelee",
    "tableSmooth": "Sujuva ja nykyaikainen",
    "comparisonOutro": "Sähköinen vuokrasopimus vähentää turhia työvaiheita ja tekee vuokrausprosessista selkeämmän kaikille osapuolille.",
    "managementTitle": "Vuokrasopimusten hallinta yhdessä paikassa",
    "managementText1": "Vuokrasopimuksen allekirjoittaminen on vain yksi osa vuokrauksen asiakirjahallintaa. Sopimus pitää myös säilyttää, löytää tarvittaessa myöhemmin ja usein jakaa oikeille henkilöille.",
    "managementText2": "Jos sopimuksia säilytetään sähköposteissa, verkkolevyillä ja paperikansioissa, oikean asiakirjan löytäminen voi olla hidasta. Tämä korostuu erityisesti yrityksissä, isännöintitoimistoissa ja kiinteistösijoittajilla, joilla on useita vuokrakohteita.",
    "managementListIntro": "Sovi.fi auttaa keskittämään asiakirjat yhteen palveluun. Tämä helpottaa esimerkiksi:",
    "managementItem1": "sopimusten hakemista",
    "managementItem2": "vuokrakohteiden asiakirjojen järjestämistä",
    "managementItem3": "allekirjoitusten seurantaa",
    "managementItem4": "liitteiden hallintaa",
    "managementItem5": "valmiiden sopimusten säilyttämistä",
    "managementOutro": "Kun sopimukset löytyvät yhdestä paikasta, vuokrauksen hallinta on tehokkaampaa ja vähemmän virhealtista.",
    "audienceTitle": "Kenelle vuokrasopimuksen sähköinen allekirjoitus sopii?",
    "audienceIntro": "Vuokrasopimuksen sähköinen allekirjoitus sopii monenlaisille käyttäjille ja organisaatioille.",
    "audiencePrivate": "Yksityiset vuokranantajat",
    "audiencePrivateText": "Yksityinen vuokranantaja voi hoitaa vuokrasopimuksen allekirjoituksen ilman tulostamista tai tapaamista. Tämä on hyödyllistä erityisesti silloin, kun vuokralainen asuu toisella paikkakunnalla tai sopimus halutaan vahvistaa nopeasti.",
    "audienceInvestors": "Kiinteistösijoittajat",
    "audienceInvestorsText": "Useita asuntoja omistavalle kiinteistösijoittajalle sähköinen allekirjoitus säästää aikaa ja tekee asiakirjojen hallinnasta järjestelmällisempää.",
    "audiencePropertyManagement": "Isännöintitoimistot",
    "audiencePropertyManagementText": "Isännöintitoimistot käsittelevät paljon asiakirjoja. Sähköinen allekirjoitus sopii vuokrasopimusten lisäksi esimerkiksi pöytäkirjoihin, urakkasopimuksiin ja taloyhtiöiden dokumentteihin.",
    "audienceAgents": "Kiinteistönvälittäjät",
    "audienceAgentsText": "Vuokra-asuntojen välittäjät voivat nopeuttaa toimeksiantoja ja vuokrausprosesseja lähettämällä sopimukset sähköisesti allekirjoitettavaksi.",
    "audienceCompanies": "Yritykset",
    "audienceCompaniesText": "Yritykset voivat allekirjoittaa toimitilojen vuokrasopimukset, varastotilojen sopimukset ja muut vuokraukseen liittyvät asiakirjat sähköisesti.",
    "challengesTitle": "Yleisimmät haasteet vuokrasopimusten allekirjoituksessa",
    "challengesText1": "Vuokrasopimuksiin liittyy usein samoja käytännön ongelmia.",
    "challengesText2": "Sopimus voi viivästyä, koska osapuolilla ei ole mahdollisuutta tavata. Vuokralaisella ei välttämättä ole tulostinta. Allekirjoitettu sopimus voi jäädä palauttamatta. Liitteet voivat kadota sähköpostiketjuihin. Myöhemmin voi olla epäselvää, mikä sopimusversio on lopullinen.",
    "challengesText3": "Sähköinen allekirjoitus auttaa vähentämään näitä ongelmia, koska prosessi on selkeä ja jäljitettävä. Allekirjoituspyyntö lähetetään järjestelmästä, allekirjoitusten etenemistä voidaan seurata ja valmis sopimus tallentuu keskitetysti.",
    "example1Title": "Käytännön esimerkki: vuokralainen toisella paikkakunnalla",
    "example1Text1": "Vuokranantaja löytää sopivan vuokralaisen Helsingissä sijaitsevaan asuntoon, mutta vuokralainen asuu vielä Tampereella. Perinteisessä prosessissa sopimus pitäisi tulostaa, allekirjoittaa, skannata ja palauttaa. Tämä voi kestää useita päiviä.",
    "example1Text2": "Sähköisessä prosessissa vuokranantaja lähettää vuokrasopimuksen allekirjoitettavaksi heti. Vuokralainen tarkistaa sopimuksen puhelimellaan ja allekirjoittaa sen saman päivän aikana. Vuokranantaja saa ilmoituksen valmiista sopimuksesta, ja vuokrasuhteen valmistelu voi jatkua ilman viivettä.",
    "example2Title": "Käytännön esimerkki: toimitilan vuokraus yritykselle",
    "example2Text1": "Yritys vuokraa uuden toimitilan, ja sopimukseen tarvitaan allekirjoitus sekä vuokranantajalta että yrityksen nimenkirjoitusoikeudelliselta henkilöltä. Osapuolet sijaitsevat eri kaupungeissa.",
    "example2Text2": "Sähköinen allekirjoitus mahdollistaa sen, että sopimus voidaan kierrättää allekirjoitettavaksi ilman fyysistä tapaamista. Prosessi on helpompi seurata, ja valmis sopimus löytyy myöhemmin samasta järjestelmästä.",
    "validityTitle": "Onko sähköisesti allekirjoitettu vuokrasopimus pätevä?",
    "validityText1": "Sähköinen allekirjoitus on useimmissa tilanteissa juridisesti sitova tapa hyväksyä vuokrasopimus. Suomessa ja EU:ssa sähköisten allekirjoitusten oikeudellista asemaa tukee eIDAS-asetus.",
    "validityText2": "Vuokrasopimuksiin voi kuitenkin liittyä tapauskohtaisia erityisvaatimuksia esimerkiksi osapuolten, kohteen, sopimustyypin tai muun lainsäädännön vuoksi. Tarvittaessa erityistilanteet kannattaa tarkistaa asiantuntijalta.",
    "validityText3": "Sovi.fi:n avulla vuokrasopimuksen allekirjoitusprosessi voidaan toteuttaa hallitusti, ja allekirjoituksesta jää dokumentoitu tapahtumahistoria.",
    "whySoviTitle": "Miksi valita Sovi.fi vuokrasopimusten sähköiseen allekirjoittamiseen?",
    "whySoviIntro": "Sovi.fi yhdistää sähköisen allekirjoituksen ja asiakirjahallinnan yhdeksi selkeäksi kokonaisuudeksi. Tämä tekee siitä hyvän vaihtoehdon vuokrasopimusten käsittelyyn. Sovi.fi:n avulla voit:",
    "whySoviItem1": "lähettää vuokrasopimuksia sähköisesti allekirjoitettavaksi",
    "whySoviItem2": "seurata allekirjoitusten etenemistä",
    "whySoviItem3": "hyödyntää valmiita sopimuspohjia",
    "whySoviItem4": "säilyttää vuokrasopimukset digitaalisesti",
    "whySoviItem5": "hallita liitteitä ja muita vuokrauksen asiakirjoja",
    "whySoviItem6": "vähentää paperityötä ja manuaalisia vaiheita",
    "whySoviOutro": "Palvelu sopii sekä satunnaiseen käyttöön että aktiiviseen sopimusten hallintaan.",
    "faqTitle": "Usein kysytyt kysymykset vuokrasopimuksen sähköisestä allekirjoituksesta",
    "faq1Question": "Voiko vuokrasopimuksen allekirjoittaa sähköisesti?",
    "faq1Answer": "Kyllä. Vuokrasopimus voidaan useimmissa tilanteissa allekirjoittaa sähköisesti. Sähköinen allekirjoitus nopeuttaa vuokrausprosessia ja mahdollistaa sopimuksen hyväksymisen ilman fyysistä tapaamista.",
    "faq2Question": "Onko sähköisesti allekirjoitettu vuokrasopimus sitova?",
    "faq2Answer": "Useimmissa tilanteissa sähköisesti allekirjoitettu vuokrasopimus on juridisesti sitova, kun allekirjoitusprosessi on toteutettu asianmukaisesti. Erityistilanteissa kannattaa tarkistaa mahdolliset lainsäädännölliset vaatimukset.",
    "faq3Question": "Voiko vuokralainen allekirjoittaa sopimuksen puhelimella?",
    "faq3Answer": "Kyllä. Vuokralainen voi allekirjoittaa vuokrasopimuksen tietokoneella, tabletilla tai älypuhelimella. Tämä helpottaa erityisesti etävuokrausta.",
    "faq4Question": "Voiko samaan vuokrasopimukseen lisätä useita allekirjoittajia?",
    "faq4Answer": "Kyllä. Samaan vuokrasopimukseen voidaan lisätä useita allekirjoittajia, kuten vuokranantaja, vuokralainen, toinen vuokralainen tai yrityksen edustaja.",
    "faq5Question": "Mitä hyötyä sähköisestä vuokrasopimuksesta on vuokranantajalle?",
    "faq5Answer": "Vuokranantaja säästää aikaa, vähentää paperityötä ja saa sopimuksen nopeammin valmiiksi. Lisäksi valmis sopimus säilyy digitaalisesti ja löytyy myöhemmin helposti.",
    "faq6Question": "Mitä hyötyä sähköisestä vuokrasopimuksesta on vuokralaiselle?",
    "faq6Answer": "Vuokralainen voi allekirjoittaa sopimuksen ajasta ja paikasta riippumatta ilman tulostamista tai skannaamista. Prosessi on nopea ja selkeä.",
    "faq7Question": "Voiko toimitilan vuokrasopimuksen allekirjoittaa sähköisesti?",
    "faq7Answer": "Kyllä. Sähköinen allekirjoitus sopii myös toimitilojen, varastojen ja muiden yritysten vuokrakohteiden sopimuksiin.",
    "faq8Question": "Voiko vuokrasopimuksen liitteet käsitellä sähköisesti?",
    "faq8Answer": "Kyllä. Vuokrasopimukseen liittyvät liitteet voidaan liittää mukaan sähköiseen prosessiin ja säilyttää yhdessä varsinaisen sopimuksen kanssa.",
    "faqMoreText": "Lisää kysymyksiä ja vastauksia löydät",
    "faqMoreLink": "UKK-sivulta",
    "ctaTitle": "Aloita vuokrasopimusten sähköinen allekirjoitus",
    "ctaText1": "Sähköinen allekirjoitus tekee vuokrasopimusten käsittelystä nopeampaa, selkeämpää ja helpommin hallittavaa. Kun sopimus voidaan lähettää, allekirjoittaa ja arkistoida digitaalisesti, vuokrausprosessi etenee ilman turhia viiveitä.",
    "ctaText2": "Sovi.fi auttaa vuokranantajia, kiinteistöalan toimijoita ja yrityksiä hoitamaan vuokrasopimukset sähköisesti yhdessä palvelussa.",
    "ctaCta": "Luo maksuton tili ja lähetä ensimmäinen vuokrasopimus allekirjoitettavaksi muutamassa minuutissa.",
    "linkEsignature": "Sähköinen allekirjoitus",
    "linkEmploymentContract": "Työsopimuksen sähköinen allekirjoitus",
    "linkFaq": "Usein kysytyt kysymykset"
  },
  "en": {
    "heroTitle": "Electronic signature for rental agreements",
    "heroLead": "Sign your rental agreement electronically — no printing or in-person meetings required",
    "heroText1": "E-signing rental agreements makes renting smoother for both landlords and tenants. There's no need to print, mail, scan, or sign in person. Instead, the rental agreement can be sent for signing online, and all parties can sign it on a computer, tablet, or phone.",
    "heroText2": "Sovi.fi offers Finnish businesses, landlords, property managers, and real estate professionals an easy way to handle electronic signatures and document management for rental agreements — all in one place.",
    "heroText3": "Electronic signatures are especially useful in rental situations where the parties aren't in the same location. The tenant can sign the agreement remotely before moving in, and the landlord can get the agreement in effect without unnecessary delays.",
    "whyTitle": "Why sign a rental agreement electronically?",
    "whyText1": "Signing a rental agreement is often a time-sensitive step. Whether it's an apartment, commercial space, or another rental property, both parties want to confirm the agreement as quickly as possible so they can plan the next steps.",
    "whyText2": "With a traditional paper process, the agreement can be delayed for many reasons. The tenant doesn't have a printer, the signature gets forgotten, scanning fails, or the agreement gets buried in email attachments. Electronic signatures reduce these issues.",
    "whyListIntro": "When a rental agreement is signed electronically:",
    "whyBenefit1": "the signing process is faster",
    "whyBenefit2": "no in-person meeting is needed",
    "whyBenefit3": "the agreement can be signed remotely",
    "whyBenefit4": "documents stay organized",
    "whyBenefit5": "completed agreements are easy to find",
    "whyBenefit6": "paper usage is reduced",
    "whyBenefit7": "the tenant experience improves",
    "whyText3": "Electronic signatures work well for individual landlords as well as organizations handling large volumes of rental agreements.",
    "processTitle": "How electronic signing of rental agreements works",
    "processIntro": "Signing a rental agreement electronically is a straightforward process.",
    "processStep1": "The rental agreement is drafted or a ready-made template is uploaded to Sovi.fi.",
    "processStep2": "The landlord adds signers, such as the tenant and any other parties.",
    "processStep3": "A signature request is sent electronically.",
    "processStep4": "The tenant opens the signature request and signs the agreement online.",
    "processStep5": "Other parties sign the agreement.",
    "processStep6": "The completed rental agreement is securely stored for future reference.",
    "processOutro": "The entire process can be completed within the same day. This is a major advantage, especially when the tenancy is about to start or there are multiple interested tenants.",
    "anywhereTitle": "Sign the rental agreement from anywhere",
    "anywhereText1": "One of the biggest advantages of electronic signatures is location independence. The tenant can sign the agreement at home, while commuting, or from another city. The landlord doesn't need to arrange a separate meeting just for signing.",
    "anywhereListIntro": "This is helpful in situations where:",
    "anywhereCase1": "the tenant is moving from another city",
    "anywhereCase2": "the landlord doesn't live near the rental property",
    "anywhereCase3": "the rental is handled through an agent",
    "anywhereCase4": "a commercial space is being leased to a company",
    "anywhereCase5": "the agreement needs to be confirmed quickly before key handover",
    "anywhereOutro": "E-signing rental agreements makes renting more flexible and reduces unnecessary waiting.",
    "documentsTitle": "What rental-related documents can be signed electronically?",
    "documentsIntro": "In addition to rental agreements, electronic signatures are suitable for many other rental-related documents. Common examples include:",
    "doc1": "residential lease agreement",
    "doc2": "commercial lease agreement",
    "doc3": "fixed-term lease agreement",
    "doc4": "open-ended lease agreement",
    "doc5": "lease renewal agreement",
    "doc6": "lease amendment",
    "doc7": "rent increase notice acceptance",
    "doc8": "termination documents",
    "doc9": "security deposit return documents",
    "doc10": "key handover protocol",
    "doc11": "property condition inspection form",
    "documentsOutro": "When all rental-related documents are managed in the same service, everything stays clear and organized — even later on.",
    "residentialTitle": "Electronic signatures for residential lease agreements",
    "residentialText1": "In residential rentals, speed and clarity are important. The tenant wants to secure the apartment, and the landlord wants a reliable agreement in place before the tenancy begins.",
    "residentialText2": "Electronic signatures simplify this step. Once the agreement is drafted, it can be sent to the tenant for signing immediately. The tenant can review the agreement at their own pace and approve it electronically — without any paper-based steps.",
    "residentialText3": "This also reduces the risk of the agreement being left unsigned or the signature being delayed as the move-in date approaches.",
    "commercialTitle": "Electronic signatures for commercial lease agreements",
    "commercialText1": "Commercial leases are often more detailed than residential agreements. They may involve usage rights, liability allocation, attachments, renovation work, lease terms, and termination conditions.",
    "commercialText2": "Electronic signatures are especially helpful in commercial rentals when multiple individuals or companies are involved. The agreement can be sent to all signers, and progress can be tracked centrally.",
    "commercialText3": "This helps ensure the agreement is finalized before the premises are taken into use.",
    "comparisonTitle": "Paper vs. electronic rental agreement",
    "tableFeature": "Feature",
    "tablePaper": "Paper rental agreement",
    "tableElectronic": "Electronic rental agreement",
    "tableRemoteSigning": "Remote signing",
    "tableDifficult": "Difficult",
    "tableEasy": "Easy",
    "tablePrinting": "Printing",
    "tableOftenNeeded": "Often needed",
    "tableNotNeeded": "Not needed",
    "tableScanning": "Scanning",
    "tableSending": "Sending the agreement",
    "tableEmailOrPost": "Email or postal mail",
    "tableOnlineRequest": "Online signature request",
    "tableTracking": "Process tracking",
    "tableManual": "Manual",
    "tableRealtime": "Real-time",
    "tableArchiving": "Archiving",
    "tablePaperFolder": "Paper folder or network drive",
    "tableDigitalManagement": "Digital document management",
    "tableFindingLater": "Finding the agreement later",
    "tableCanBeSlow": "Can be slow",
    "tableFast": "Fast",
    "tableTenantExperience": "Tenant experience",
    "tableVaries": "Varies",
    "tableSmooth": "Smooth and modern",
    "comparisonOutro": "Electronic rental agreements eliminate unnecessary steps and make the rental process clearer for all parties.",
    "managementTitle": "Manage rental agreements in one place",
    "managementText1": "Signing the rental agreement is just one part of rental document management. The agreement also needs to be stored, retrieved when needed, and often shared with the right people.",
    "managementText2": "If agreements are scattered across emails, network drives, and paper folders, finding the right document can be slow. This is especially true for companies, property management firms, and real estate investors with multiple rental properties.",
    "managementListIntro": "Sovi.fi helps centralize documents in one service. This makes it easier to:",
    "managementItem1": "search for agreements",
    "managementItem2": "organize documents by property",
    "managementItem3": "track signature progress",
    "managementItem4": "manage attachments",
    "managementItem5": "store completed agreements",
    "managementOutro": "When all agreements are in one place, rental management is more efficient and less error-prone.",
    "audienceTitle": "Who is electronic signing of rental agreements for?",
    "audienceIntro": "Electronic signing of rental agreements is suitable for a wide range of users and organizations.",
    "audiencePrivate": "Private landlords",
    "audiencePrivateText": "A private landlord can handle rental agreement signing without printing or meetings. This is especially useful when the tenant lives in another city or the agreement needs to be confirmed quickly.",
    "audienceInvestors": "Real estate investors",
    "audienceInvestorsText": "For real estate investors with multiple properties, electronic signatures save time and make document management more systematic.",
    "audiencePropertyManagement": "Property management companies",
    "audiencePropertyManagementText": "Property management companies handle a large volume of documents. Electronic signatures are useful for rental agreements as well as meeting minutes, contractor agreements, and housing association documents.",
    "audienceAgents": "Real estate agents",
    "audienceAgentsText": "Rental agents can speed up assignments and leasing processes by sending agreements for electronic signing.",
    "audienceCompanies": "Companies",
    "audienceCompaniesText": "Companies can electronically sign commercial leases, warehouse agreements, and other rental-related documents.",
    "challengesTitle": "Common challenges in signing rental agreements",
    "challengesText1": "Rental agreements often involve the same practical problems.",
    "challengesText2": "The agreement may be delayed because the parties can't meet in person. The tenant may not have a printer. A signed agreement may not get returned. Attachments can get lost in email threads. Later, it may be unclear which version of the agreement is the final one.",
    "challengesText3": "Electronic signatures help reduce these issues because the process is clear and traceable. The signature request is sent from the system, progress can be monitored, and the completed agreement is stored centrally.",
    "example1Title": "Practical example: tenant in another city",
    "example1Text1": "A landlord finds a suitable tenant for an apartment in Helsinki, but the tenant still lives in Tampere. In a traditional process, the agreement would need to be printed, signed, scanned, and returned. This can take several days.",
    "example1Text2": "With electronic signing, the landlord sends the rental agreement for signing right away. The tenant reviews the agreement on their phone and signs it the same day. The landlord receives a notification that the agreement is complete, and preparations for the tenancy can continue without delay.",
    "example2Title": "Practical example: leasing commercial space to a company",
    "example2Text1": "A company is leasing a new office space, and the agreement requires signatures from both the landlord and the company's authorized signatory. The parties are located in different cities.",
    "example2Text2": "Electronic signing allows the agreement to be circulated for signatures without an in-person meeting. The process is easier to track, and the completed agreement can be found later in the same system.",
    "validityTitle": "Is an electronically signed rental agreement legally valid?",
    "validityText1": "In most situations, an electronic signature is a legally binding way to approve a rental agreement. In Finland and the EU, the legal status of electronic signatures is supported by the eIDAS regulation.",
    "validityText2": "However, rental agreements may have case-specific requirements depending on the parties, property, agreement type, or other legislation. When in doubt, it's worth consulting a professional about special circumstances.",
    "validityText3": "With Sovi.fi, the rental agreement signing process can be carried out in a controlled manner, and the signature leaves a documented event history.",
    "whySoviTitle": "Why choose Sovi.fi for signing rental agreements electronically?",
    "whySoviIntro": "Sovi.fi combines electronic signatures and document management into one clear solution. This makes it a great option for handling rental agreements. With Sovi.fi you can:",
    "whySoviItem1": "send rental agreements for electronic signing",
    "whySoviItem2": "track signature progress",
    "whySoviItem3": "use ready-made agreement templates",
    "whySoviItem4": "store rental agreements digitally",
    "whySoviItem5": "manage attachments and other rental documents",
    "whySoviItem6": "reduce paperwork and manual steps",
    "whySoviOutro": "The service works for both occasional use and active agreement management.",
    "faqTitle": "Frequently asked questions about electronic signing of rental agreements",
    "faq1Question": "Can a rental agreement be signed electronically?",
    "faq1Answer": "Yes. In most situations, a rental agreement can be signed electronically. Electronic signatures speed up the rental process and allow the agreement to be approved without an in-person meeting.",
    "faq2Question": "Is an electronically signed rental agreement binding?",
    "faq2Answer": "In most situations, an electronically signed rental agreement is legally binding when the signing process is carried out properly. In special cases, it's worth checking any legal requirements that may apply.",
    "faq3Question": "Can the tenant sign the agreement on their phone?",
    "faq3Answer": "Yes. The tenant can sign the rental agreement on a computer, tablet, or smartphone. This is especially helpful for remote rentals.",
    "faq4Question": "Can multiple signers be added to the same rental agreement?",
    "faq4Answer": "Yes. Multiple signers can be added to the same rental agreement, such as the landlord, tenant, a second tenant, or a company representative.",
    "faq5Question": "What are the benefits of an electronic rental agreement for landlords?",
    "faq5Answer": "Landlords save time, reduce paperwork, and get the agreement completed faster. The finished agreement is stored digitally and easy to find later.",
    "faq6Question": "What are the benefits of an electronic rental agreement for tenants?",
    "faq6Answer": "Tenants can sign the agreement anytime, anywhere — without printing or scanning. The process is fast and clear.",
    "faq7Question": "Can a commercial lease be signed electronically?",
    "faq7Answer": "Yes. Electronic signatures are also suitable for commercial premises, warehouses, and other business rental agreements.",
    "faq8Question": "Can rental agreement attachments be handled electronically?",
    "faq8Answer": "Yes. Attachments related to the rental agreement can be included in the electronic process and stored together with the main agreement.",
    "faqMoreText": "Find more questions and answers on the",
    "faqMoreLink": "FAQ page",
    "ctaTitle": "Get started with electronic signing of rental agreements",
    "ctaText1": "Electronic signatures make handling rental agreements faster, clearer, and easier to manage. When agreements can be sent, signed, and archived digitally, the rental process moves forward without unnecessary delays.",
    "ctaText2": "Sovi.fi helps landlords, real estate professionals, and companies handle rental agreements electronically in one service.",
    "ctaCta": "Create a free account and send your first rental agreement for signing in just a few minutes.",
    "linkEsignature": "Electronic signature",
    "linkEmploymentContract": "Electronic signing of employment contracts",
    "linkFaq": "Frequently asked questions"
  }
}
</i18n>
